import * as T from './redux.actionTypes';
import { DEFAULT_PAGE_SIZE } from './common';

const initialState = {
    clients: [],
    loading: false,
    error: null,

    // Detail (full client with nested relations)
    detail: null,
    detailLoading: false,
    detailError: null,

    // Team dropdown options
    teamOptions: [],
    teamOptionsLoading: false,

    // Employee dropdown options
    employeeOptions: [],
    employeeOptionsLoading: false,

    // Pagination
    pagination: { limit: DEFAULT_PAGE_SIZE, offset: 1, total: 0 },

    // Filters
    filters: { search: '', type: '', isActive: '', employeeEmail: '', teamId: '' },

    // Sorting
    sort: { sortBy: 'updatedAt', sortDescending: true },

    // Dialogs
    isClientDialogOpen: false,
    isDeleteDialogOpen: false,
    isDetailDialogOpen: false,
    selectedClient: null,

    // Set after a client is created so the UI can navigate to its detail page
    lastCreatedClientId: null
};

const replaceById = (arr, item) => {
    if (!Array.isArray(arr) || !item) return arr;
    let changed = false;
    const next = arr.map((x) => {
        if (x && x.id === item.id) { changed = true; return item; }
        return x;
    });
    return changed ? next : [...arr, item];
};

const updateDetailField = (state, key, updater) => {
    if (!state.detail) return state;
    const current = state.detail[key];
    const next = updater(Array.isArray(current) ? current : []);
    if (next === current) return state;
    return { ...state, detail: { ...state.detail, [key]: next } };
};

const clientReducer = (state = initialState, action) => {
    switch (action.type) {
        case T.FETCH_CLIENTS:
            return { ...state, loading: true, error: null };
        case T.FETCH_CLIENTS_SUCCESS:
            return {
                ...state,
                loading: false,
                clients: action.payload.data || [],
                pagination: { ...state.pagination, total: action.payload.total || 0 }
            };
        case T.FETCH_CLIENTS_FAILURE:
            return { ...state, loading: false, error: action.payload };

        case T.FETCH_CLIENT_DETAIL:
            return { ...state, detailLoading: true, detailError: null };
        case T.FETCH_CLIENT_DETAIL_SUCCESS:
            return { ...state, detailLoading: false, detail: action.payload };
        case T.FETCH_CLIENT_DETAIL_FAILURE:
            return { ...state, detailLoading: false, detailError: action.payload };
        case T.CLEAR_CLIENT_DETAIL:
            return { ...state, detail: null, detailError: null };

        case T.CREATE_CLIENT:
        case T.UPDATE_CLIENT:
        case T.DELETE_CLIENT:
            return { ...state, loading: true, error: null };
        case T.CREATE_CLIENT_SUCCESS:
            return {
                ...state,
                loading: false,
                lastCreatedClientId: action.payload && action.payload.id ? action.payload.id : state.lastCreatedClientId
            };
        case T.UPDATE_CLIENT_SUCCESS: {
            const updated = action.payload;
            const nextClients = updated && updated.id ? replaceById(state.clients, updated) : state.clients;
            const nextDetail = state.detail && updated && state.detail.id === updated.id ? { ...state.detail, ...updated } : state.detail;
            return { ...state, loading: false, clients: nextClients, detail: nextDetail };
        }
        case T.DELETE_CLIENT_SUCCESS:
            return { ...state, loading: false };
        case T.CREATE_CLIENT_FAILURE:
        case T.UPDATE_CLIENT_FAILURE:
        case T.DELETE_CLIENT_FAILURE:
            return { ...state, loading: false, error: action.payload };

        case T.CLEAR_LAST_CREATED_CLIENT:
            return { ...state, lastCreatedClientId: null };

        // ===== Sub-resources: granular updates so only the affected section re-renders =====
        case T.ADD_CLIENT_WEBSITE_SUCCESS:
            return updateDetailField(state, 'websites', (arr) => [...arr, action.payload]);
        case T.UPDATE_CLIENT_WEBSITE_SUCCESS:
            return updateDetailField(state, 'websites', (arr) => replaceById(arr, action.payload));
        case T.DELETE_CLIENT_WEBSITE_SUCCESS:
            return updateDetailField(state, 'websites', (arr) => arr.filter((x) => x.id !== action.payload.websiteId));

        case T.ADD_CLIENT_PERIOD_SUCCESS:
            return updateDetailField(state, 'workPeriods', (arr) => [...arr, action.payload]);
        case T.UPDATE_CLIENT_PERIOD_SUCCESS:
            return updateDetailField(state, 'workPeriods', (arr) => replaceById(arr, action.payload));
        case T.DELETE_CLIENT_PERIOD_SUCCESS:
            return updateDetailField(state, 'workPeriods', (arr) => arr.filter((x) => x.id !== action.payload.periodId));

        case T.ADD_CLIENT_GIT_REPO_SUCCESS:
            return updateDetailField(state, 'gitRepos', (arr) => [...arr, action.payload]);
        case T.UPDATE_CLIENT_GIT_REPO_SUCCESS:
            return updateDetailField(state, 'gitRepos', (arr) => replaceById(arr, action.payload));
        case T.DELETE_CLIENT_GIT_REPO_SUCCESS:
            return updateDetailField(state, 'gitRepos', (arr) => arr.filter((x) => x.id !== action.payload.repoId));

        case T.ADD_CLIENT_STACK_SUCCESS:
            return updateDetailField(state, 'stacks', (arr) => [...arr, action.payload]);
        case T.UPDATE_CLIENT_STACK_SUCCESS:
            return updateDetailField(state, 'stacks', (arr) => replaceById(arr, action.payload));
        case T.DELETE_CLIENT_STACK_SUCCESS:
            return updateDetailField(state, 'stacks', (arr) => arr.filter((x) => x.id !== action.payload.stackId));

        case T.ASSIGN_EMPLOYEE_SUCCESS: {
            return updateDetailField(state, 'employees', (arr) => {
                const item = action.payload;
                if (!item) return arr;
                // Enrich the newly assigned record with name data from employeeOptions
                // so the name + email show immediately without a page reload.
                const email = (item.employeeEmail || '').toLowerCase();
                const opt = (state.employeeOptions || []).find(
                    (o) => (o.user?.email || o.employeeId || '').toLowerCase() === email
                );
                const enriched = opt
                    ? {
                          ...item,
                          firstname: item.firstname || opt.firstname,
                          lastname: item.lastname || opt.lastname,
                          profile: item.profile || opt.profile
                      }
                    : item;
                const idx = arr.findIndex((x) => (x.employeeEmail || '').toLowerCase() === email);
                if (idx === -1) return [...arr, enriched];
                const next = arr.slice(); next[idx] = enriched; return next;
            });
        }
        case T.UPDATE_EMPLOYEE_ASSIGNMENT_SUCCESS:
            return updateDetailField(state, 'employees', (arr) => {
                const item = action.payload;
                if (!item) return arr;
                const idx = arr.findIndex((x) => (x.employeeEmail || '').toLowerCase() === (item.employeeEmail || '').toLowerCase());
                if (idx === -1) return arr;
                const next = arr.slice(); next[idx] = item; return next;
            });
        case T.UNASSIGN_EMPLOYEE_SUCCESS:
            return updateDetailField(state, 'employees', (arr) =>
                arr.filter((x) => (x.employeeEmail || '').toLowerCase() !== (action.payload.email || '').toLowerCase())
            );

        case T.ASSIGN_TEAM_SUCCESS:
            return updateDetailField(state, 'teams', (arr) => {
                const item = action.payload;
                if (!item) return arr;
                const teamId = item.teamId || (item.team && item.team.id);
                // Enrich with full team data from teamOptions so name + members show immediately
                const opt = (state.teamOptions || []).find((o) => o.id === teamId);
                const enriched = opt
                    ? { ...item, team: { ...(item.team || {}), ...opt, members: opt.members || item.team?.members } }
                    : item;
                const idx = arr.findIndex((x) => (x.teamId || (x.team && x.team.id)) === teamId);
                if (idx === -1) return [...arr, enriched];
                const next = arr.slice(); next[idx] = enriched; return next;
            });
        case T.UNASSIGN_TEAM_SUCCESS:
            return updateDetailField(state, 'teams', (arr) =>
                arr.filter((x) => (x.teamId || (x.team && x.team.id)) !== action.payload.teamId)
            );

        case T.FETCH_TEAM_OPTIONS:
            return { ...state, teamOptionsLoading: true };
        case T.FETCH_TEAM_OPTIONS_SUCCESS:
            return { ...state, teamOptionsLoading: false, teamOptions: Array.isArray(action.payload) ? action.payload : [] };
        case T.FETCH_TEAM_OPTIONS_FAILURE:
            return { ...state, teamOptionsLoading: false };

        case T.FETCH_EMPLOYEE_OPTIONS:
            return { ...state, employeeOptionsLoading: true };
        case T.FETCH_EMPLOYEE_OPTIONS_SUCCESS:
            return { ...state, employeeOptionsLoading: false, employeeOptions: Array.isArray(action.payload) ? action.payload : [] };
        case T.FETCH_EMPLOYEE_OPTIONS_FAILURE:
            return { ...state, employeeOptionsLoading: false };

        case T.OPEN_CLIENT_DIALOG:
            return { ...state, isClientDialogOpen: true, selectedClient: action.payload };
        case T.CLOSE_CLIENT_DIALOG:
            return { ...state, isClientDialogOpen: false, selectedClient: null };

        case T.OPEN_DELETE_DIALOG:
            return { ...state, isDeleteDialogOpen: true, selectedClient: action.payload };
        case T.CLOSE_DELETE_DIALOG:
            return { ...state, isDeleteDialogOpen: false, selectedClient: null };

        case T.OPEN_DETAIL_DIALOG:
            return { ...state, isDetailDialogOpen: true, selectedClient: action.payload };
        case T.CLOSE_DETAIL_DIALOG:
            return { ...state, isDetailDialogOpen: false, selectedClient: null, detail: null };

        case T.SET_FILTERS:
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case T.SET_PAGINATION:
            return { ...state, pagination: { ...state.pagination, ...action.payload } };
        case T.SET_SORT:
            return { ...state, sort: { ...state.sort, ...action.payload } };

        default:
            return state;
    }
};

export default clientReducer;
